import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import type { Permission } from '../api/types';
import { useAuthStore } from '../auth/authStore';
import { grantsAny } from '../navigation/navigation';
import { ForbiddenState } from './states/StateViews';

/**
 * Route guard.
 *
 * Two distinct outcomes, deliberately not collapsed into one: an unauthenticated visitor is sent
 * to sign in and returned to where they were heading, while an authenticated one who lacks the
 * permission is told so and stays put. Redirecting the second case to sign-in would suggest their
 * session had expired, which sends staff to reset passwords that were never the problem.
 *
 * This is convenience and clarity, not enforcement. The API refuses the request regardless of what
 * the browser renders.
 */
export function RequirePermission({
  children,
  requires = [],
}: {
  readonly children: ReactNode;
  readonly requires?: readonly Permission[];
}) {
  const phase = useAuthStore((state) => state.phase);
  const permissions = useAuthStore((state) => state.permissions);
  const location = useLocation();

  if (phase !== 'authenticated') {
    return <Navigate to="/sign-in" replace state={{ from: location.pathname }} />;
  }

  if (!grantsAny(permissions, requires)) {
    return <ForbiddenState />;
  }

  return <>{children}</>;
}
